export const AGENT_ACTIVITY_IDLE_MS = 5 * 60 * 1000;
export const AGENT_ACTIVITY_EVENT_THROTTLE_MS = 1000;
export const AGENT_ACTIVITY_TAB_SYNC_MS = 5000;
export const AGENT_ACTIVITY_PRESENCE_THROTTLE_MS = 60 * 1000;
export const AGENT_ACTIVITY_PRESENCE_RETRY_MS = 5000;

const ACTIVITY_EVENTS = [
  'pointermove',
  'pointerdown',
  'click',
  'keydown',
  'touchstart',
  'wheel',
];

const readTimestamp = key => {
  try {
    return Number(window.localStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
};

const readPresence = key => {
  try {
    const rawValue = window.localStorage.getItem(key);
    if (!rawValue) return { sentAt: 0, activityAt: 0 };

    const legacyTimestamp = Number(rawValue);
    if (legacyTimestamp) {
      return { sentAt: legacyTimestamp, activityAt: legacyTimestamp };
    }

    const value = JSON.parse(rawValue);
    return {
      sentAt: Number(value.sentAt) || 0,
      activityAt: Number(value.activityAt) || 0,
    };
  } catch {
    return { sentAt: 0, activityAt: 0 };
  }
};

const writeTimestamp = (key, value) => {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Activity remains functional in this tab when storage is unavailable.
  }
};

const writePresence = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Presence delivery remains functional when storage is unavailable.
  }
};

class AgentActivityTracker {
  constructor({ accountId, userId, onPresence, onStateChange }) {
    this.accountId = accountId;
    this.userId = userId;
    this.onPresence = onPresence;
    this.onStateChange = onStateChange;
    this.scope = `${accountId}:${userId}`;
    this.activityKey = `cw:agent-activity:${this.scope}`;
    this.presenceKey = `cw:agent-activity-presence:${this.scope}`;
    this.channelName = `cw-agent-activity:${this.scope}`;
    this.presenceLockName = `cw-agent-activity-presence-lock:${this.scope}`;
    this.lastActivityAt = 0;
    this.lastHandledAt = 0;
    this.lastSyncedAt = 0;
    this.lastPresenceAt = 0;
    this.lastPresenceActivityAt = 0;
    this.lastPresenceAttemptAt = 0;
    this.isActive = false;
    this.started = false;
    this.activityPending = false;
    this.presencePending = false;
    this.presenceRequestPending = false;
    this.forcePresencePending = false;
    this.idleTimer = null;
    this.syncTimer = null;
    this.presenceTimer = null;
    this.channel = null;
  }

  start() {
    if (this.started) return;

    this.started = true;
    this.setupChannel();
    ACTIVITY_EVENTS.forEach(eventName =>
      document.addEventListener(eventName, this.handleActivity, {
        passive: true,
      })
    );
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('focus', this.handleActivity, { passive: true });
    window.addEventListener('storage', this.handleStorage);

    const sharedActivity = readTimestamp(this.activityKey);
    if (sharedActivity > Date.now() - AGENT_ACTIVITY_IDLE_MS) {
      this.applyActivity(sharedActivity, { remote: true });
    }
  }

  stop({ flushPresence = false } = {}) {
    if (!this.started) return;

    if (flushPresence) {
      this.flushActivitySync();
      this.flushPresence({ force: true, coordinated: false });
    }

    ACTIVITY_EVENTS.forEach(eventName =>
      document.removeEventListener(eventName, this.handleActivity)
    );
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('focus', this.handleActivity);
    window.removeEventListener('storage', this.handleStorage);
    this.channel?.close();
    this.channel = null;
    this.clearTimers();
    this.started = false;
  }

  handleActivity = () => {
    const timestamp = Date.now();
    if (timestamp - this.lastHandledAt < AGENT_ACTIVITY_EVENT_THROTTLE_MS) {
      return;
    }

    this.lastHandledAt = timestamp;
    this.applyActivity(timestamp, { remote: false });
  };

  handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.flushActivitySync();
      this.flushPresence({ force: true, coordinated: false });
    }
  };

  handlePageHide = () => {
    this.flushActivitySync();
    this.flushPresence({ force: true, coordinated: false });
  };

  handleStorage = event => {
    if (event.key === this.activityKey) {
      this.applyActivity(Number(event.newValue), { remote: true });
    }
    if (event.key === this.presenceKey) {
      const presence = readPresence(this.presenceKey);
      this.applySharedPresence(presence);
    }
  };

  handleChannelMessage = event => {
    if (event.data?.type === 'activity') {
      this.applyActivity(Number(event.data.timestamp), { remote: true });
    }
  };

  applyActivity(timestamp, { remote }) {
    if (!Number.isFinite(timestamp) || timestamp <= this.lastActivityAt) return;

    const wasActive = this.isActive;
    this.lastActivityAt = timestamp;
    this.setActive(true);
    this.scheduleIdleCheck();

    if (remote) return;

    this.activityPending = true;
    this.scheduleActivitySync();
    if (!wasActive) {
      this.sendPresence();
    } else {
      this.schedulePresence();
    }
  }

  setActive(value) {
    if (this.isActive === value) return;

    this.isActive = value;
    this.onStateChange(value ? 'online' : 'offline');
  }

  scheduleIdleCheck() {
    clearTimeout(this.idleTimer);
    const delay = Math.max(
      this.lastActivityAt + AGENT_ACTIVITY_IDLE_MS - Date.now(),
      0
    );
    this.idleTimer = setTimeout(this.checkIdle, delay + 10);
  }

  checkIdle = () => {
    const sharedActivity = readTimestamp(this.activityKey);
    if (sharedActivity > this.lastActivityAt) {
      this.applyActivity(sharedActivity, { remote: true });
      return;
    }
    if (Date.now() - this.lastActivityAt < AGENT_ACTIVITY_IDLE_MS) {
      this.scheduleIdleCheck();
      return;
    }

    this.setActive(false);
  };

  scheduleActivitySync() {
    const remaining =
      AGENT_ACTIVITY_TAB_SYNC_MS - (Date.now() - this.lastSyncedAt);
    if (remaining <= 0) {
      this.flushActivitySync();
      return;
    }
    if (!this.syncTimer) {
      this.syncTimer = setTimeout(this.flushActivitySync, remaining);
    }
  }

  flushActivitySync = () => {
    clearTimeout(this.syncTimer);
    this.syncTimer = null;
    if (!this.activityPending) return;

    this.activityPending = false;
    this.lastSyncedAt = Date.now();
    writeTimestamp(this.activityKey, this.lastActivityAt);
    this.channel?.postMessage({
      type: 'activity',
      timestamp: this.lastActivityAt,
    });
  };

  schedulePresence() {
    const sharedPresence = readPresence(this.presenceKey);
    this.applySharedPresence(sharedPresence);
    const remaining =
      AGENT_ACTIVITY_PRESENCE_THROTTLE_MS - (Date.now() - this.lastPresenceAt);
    if (remaining <= 0) {
      this.sendPresence();
      return;
    }
    if (!this.presenceTimer) {
      this.presenceTimer = setTimeout(this.flushPresence, remaining);
    }
  }

  flushPresence = ({ force = false, coordinated = true } = {}) => {
    clearTimeout(this.presenceTimer);
    this.presenceTimer = null;
    if (!this.isActive || this.lastActivityAt <= this.lastPresenceActivityAt) {
      return;
    }

    if (coordinated) {
      this.sendPresence({ force });
    } else {
      this.deliverPresence({ force });
    }
  };

  sendPresence({ force = false } = {}) {
    if (this.presenceRequestPending) {
      this.forcePresencePending ||= force;
      return;
    }

    if (navigator.locks?.request) {
      this.presenceRequestPending = true;
      navigator.locks
        .request(this.presenceLockName, () => this.deliverPresence({ force }))
        .catch(() => this.deliverPresence({ force }))
        .finally(() => {
          this.presenceRequestPending = false;
          if (this.forcePresencePending) {
            this.forcePresencePending = false;
            this.sendPresence({ force: true });
          }
        });
      return;
    }

    this.deliverPresence({ force });
  }

  deliverPresence({ force = false } = {}) {
    const timestamp = Date.now();
    const sharedPresence = readPresence(this.presenceKey);
    this.applySharedPresence(sharedPresence);
    if (sharedPresence.activityAt >= this.lastActivityAt) {
      this.presencePending = false;
      return true;
    }
    if (
      !force &&
      this.isActive &&
      sharedPresence.sentAt &&
      timestamp - sharedPresence.sentAt < AGENT_ACTIVITY_PRESENCE_THROTTLE_MS
    ) {
      this.schedulePresence();
      return true;
    }

    this.lastPresenceAttemptAt = timestamp;
    const sent = this.onPresence(Math.max(timestamp - this.lastActivityAt, 0));
    if (sent === false) {
      this.presencePending = true;
      this.schedulePresenceRetry();
      return false;
    }

    const presence = { sentAt: timestamp, activityAt: this.lastActivityAt };
    this.applySharedPresence(presence);
    writePresence(this.presenceKey, presence);
    this.presencePending = false;
    return true;
  }

  applySharedPresence({ sentAt, activityAt }) {
    this.lastPresenceAt = Math.max(this.lastPresenceAt, sentAt);
    this.lastPresenceActivityAt = Math.max(
      this.lastPresenceActivityAt,
      activityAt
    );
  }

  schedulePresenceRetry() {
    clearTimeout(this.presenceTimer);
    const remaining = Math.max(
      AGENT_ACTIVITY_PRESENCE_RETRY_MS -
        (Date.now() - this.lastPresenceAttemptAt),
      0
    );
    this.presenceTimer = setTimeout(
      () => this.flushPresence({ force: true }),
      remaining
    );
  }

  handleConnectionReady() {
    if (!this.started || !this.isActive) return;

    this.flushPresence({ force: true });
  }

  setupChannel() {
    if (!window.BroadcastChannel) return;

    this.channel = new window.BroadcastChannel(this.channelName);
    this.channel.addEventListener('message', this.handleChannelMessage);
  }

  clearTimers() {
    clearTimeout(this.idleTimer);
    clearTimeout(this.syncTimer);
    clearTimeout(this.presenceTimer);
    this.idleTimer = null;
    this.syncTimer = null;
    this.presenceTimer = null;
  }
}

export default AgentActivityTracker;
